import { useCallback, useMemo } from 'react'
import { ReadSyncValidationsDocument } from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { SyncIssue, useSyncIssues } from 'modules/integrations/hooks/SyncContext/issues'

interface FetchIssuesResult {
  hasIssues: boolean
}

interface UseSyncValidationResult {
  fetchIssues: () => Promise<FetchIssuesResult | undefined>
  issues: SyncIssue[]
  issuesLoading: boolean
  anyValidSyncable: boolean
  totalSyncables: number
}

export const useSyncValidation = (): UseSyncValidationResult => {
  const {
    syncEnabled,
    marketplaceIntegration,
    syncParameters,
    dataCollectionGraphqlJsonString,
    configuration,
    isBookkeeperView,
    companyId,
  } = usePrivateIntegrationsSyncContext()

  const { issuesByKey } = configuration
  const [
    fetchSyncValidationsQuery,
    { data: syncValidationsData, loading: issuesLoading, networkActivity },
  ] = useLazyQuery(ReadSyncValidationsDocument)

  const validationNode = syncValidationsData?.integrations?.syncValidationsConnection.nodes[0]
  const rawIssues = useMemo(() => validationNode?.issues ?? [], [validationNode])
  const totalSyncables = validationNode?.totalSyncables ?? 0
  const invalidSyncables = validationNode?.invalidSyncables ?? 0
  const anyValidSyncable = invalidSyncables < totalSyncables

  const fetchIssues = useCallback(async (): Promise<FetchIssuesResult | undefined> => {
    if (!syncEnabled || !marketplaceIntegration || !companyId) return undefined

    const result = await fetchSyncValidationsQuery({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        syncParameters: syncParameters(),
        graphqlVariablesJson: dataCollectionGraphqlJsonString,
      },
      context: {
        isBookkeeperView,
      },
    })

    const node = result.data?.integrations?.syncValidationsConnection.nodes[0]
    const fetchedIssues = node?.issues ?? []

    return { hasIssues: fetchedIssues.length > 0 }
  }, [
    syncEnabled,
    marketplaceIntegration,
    companyId,
    dataCollectionGraphqlJsonString,
    configuration.origin,
    syncParameters,
    isBookkeeperView,
    fetchSyncValidationsQuery,
  ])

  return {
    fetchIssues,
    issues: useSyncIssues(rawIssues, marketplaceIntegration, issuesByKey, true, companyId),
    issuesLoading: networkActivity || issuesLoading,
    anyValidSyncable,
    totalSyncables,
  }
}
